import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { QuoteFormField, QuoteFormRequest, QuoteFormSection } from '../../../models/ai-quote-chat.models';
import { QuestionType } from '../../../enums/ai-quote-chat.enum';

@Component({
  selector: 'app-ai-quote-form',
  templateUrl: './ai-quote-form.component.html',
  styleUrl: './ai-quote-form.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AiQuoteFormComponent implements OnInit, OnChanges {

  private _quoteFormRequest!: QuoteFormRequest;
  @Input()
  set quoteFormRequest(value: QuoteFormRequest) {
    this._quoteFormRequest = value;
    this.normalizeSections();
    if (this.quoteForm) {
      this.buildForm();
    }
  }
  get quoteFormRequest(): QuoteFormRequest {
    return this._quoteFormRequest;
  }
  @Input() isMobileApp = false;

  private _isReadOnly = false;
  @Input()
  set isReadOnly(value: boolean) {
    const nextValue = !!value;
    const enteringReadOnly = nextValue && !this._isReadOnly;
    this._isReadOnly = nextValue;

    if (enteringReadOnly && !this.hasSubmittedSelection) {
      this.resetAllValues();
    }
  }

  get isReadOnly(): boolean {
    return this._isReadOnly;
  }

  @Output() formSubmit = new EventEmitter<string>();

  quoteForm!: FormGroup;
  viewSections: Array<{
    title: string;
    fields: Array<{
      label: string;
      name: string;
      placeholder: string;
      required: boolean;
      type: QuestionType;
      options: { label: string; value: string }[];
    }>;
  }> = [];

  readonly questionType = QuestionType;
  hideCancelButton = true; //Hide cancel button for now. Can be reverted back if needed.
  private hasSubmittedSelection = false;

  constructor(private fb: FormBuilder) { }

  ngOnInit(): void {
    this.quoteForm = this.fb.group({});
    this.normalizeSections();
    this.buildForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['quoteFormRequest'] && this.quoteForm) {
      this.normalizeSections();
      this.buildForm();
    }
  }

  get sections(): Array<{
    title: string;
    fields: Array<{
      label: string;
      name: string;
      placeholder: string;
      required: boolean;
      type: QuestionType;
      options: { label: string; value: string }[];
    }>;
  }> {
    return this.viewSections;
  }

  private buildForm(): void {
    if (!this.quoteForm) {
      return;
    }

    this.hasSubmittedSelection = false;

    Object.keys(this.quoteForm.controls).forEach((controlName) => {
      this.quoteForm.removeControl(controlName);
    });

    this.sections.forEach((section) => {
      section.fields.forEach((field) => {
        const validators = field.required ? [Validators.required] : [];
        const initialValue = field.type === QuestionType.RADIO ? null : '';

        this.quoteForm.addControl(field.name, this.fb.control(initialValue, validators));
      });
    });
  }

  private normalizeSections(): void {
    this.viewSections = (this._quoteFormRequest?.sections ?? []).map(section => ({
      title: section.title,
      fields: (section.fields ?? []).map(field => ({
        label: field.label,
        name: field.name,
        placeholder: field.placeholder,
        required: field.required,
        type: field.type,
        options: this.normalizeOptions(field.options)
      }))
    }));
  }

  onSubmit(): void {
    this.quoteForm.markAllAsTouched();

    if (this.quoteForm.invalid) {
      return;
    }

    this.hasSubmittedSelection = true;
    this.formSubmit.emit(this.buildSubmissionText());
    this.isReadOnly = true;
  }

  cancel(): void {
    this.resetAllValues();
    this.formSubmit.emit('Site Quote form submission cancelled by user.');
    this.isReadOnly = true;
  }

  isChecked(fieldName: string, value: string): boolean {
    return this.quoteForm.value?.[fieldName] === value;
  }

  onSelectionChange(fieldName: string, value: string): void {
    this.hasSubmittedSelection = false;
    this.quoteForm.patchValue({ [fieldName]: value });
  }

  private resetAllValues(): void {
    this.hasSubmittedSelection = false;
    if (this.quoteForm) {
      this.quoteForm.reset();
    }
  }

  getDisplayLabel(value: string, options: { label: string, value: string }[]): string {
    const option = options.find(opt => opt.value === value);
    return option ? option.label : '';
  }

  getSelectedOptions(value: string | string[] | null, options: { label: string, value: string }[]): { label: string, value: string }[] {
    if (!value) {
      return [];
    }

    const selectedValues = Array.isArray(value) ? value : [value];
    return options.filter(option => selectedValues.includes(option.value));
  }

  getSectionOptions(field: QuoteFormField): { label: string; value: string }[] {
    return this.normalizeOptions(field.options);
  }

  isFieldInvalid(fieldName: string): boolean {
    const control = this.quoteForm.get(fieldName);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  getFieldErrorTooltip(fieldName: string): string {
    const control = this.quoteForm.get(fieldName);
    if (!control || !control.errors) {
      return '';
    }

    if (control.errors['required']) {
      return 'This field is required.';
    }

    return 'Please enter a valid value.';
  }

  isSelected(fieldName: string, value: string): boolean {
    return this.quoteForm.value?.[fieldName] === value;
  }

  // Checks if the field has a read-only value
  hasReadOnlyValue(fieldName: string): boolean {
    const value = this.quoteForm.value?.[fieldName];
    return value !== null && value !== undefined && value !== '';
  }

  // Show tooltip for mobile devices on click event
  showTooltipForMobile(tooltipRef: any) {
    setTimeout(() => {
      tooltipRef.show();
      setTimeout(() => {
        tooltipRef.hide();
      }, 1500);
    }, 0);
  }

  private buildSubmissionText(): string {
    const pairs: string[] = [];

    this.sections.forEach((section) => {
      section.fields.forEach((field) => {
        const rawValue = this.quoteForm.value?.[field.name];
        if (rawValue === null || rawValue === undefined || rawValue === '') {
          return;
        }

        pairs.push(`${field.name}=${String(rawValue)}`);
      });
    });

    return pairs.join(',');
  }

  private normalizeOptions(options: QuoteFormField['options']): { label: string; value: string }[] {
    return (options ?? []).map((option: any) => {
      if (typeof option === 'string') {
        return { label: option, value: option };
      }

      return {
        label: option.label ?? option.value ?? '',
        value: option.value ?? option.label ?? ''
      };
    }).filter(option => !!option.value);
  }
}
